/**
 * `@dxf-ai-editor/cad-core` — per-kind LEAF bounds math (DEFUSE S10b, split
 * out of `bounds.ts`).
 *
 * "Leaf" = every kind EXCEPT `Block` (INSERT) — the one kind whose bounds
 * require resolving a referenced block DEFINITION (a graph, not a leaf
 * computation; owned by `blockGeometry.ts`). Both `bounds.ts` (top-level
 * `getEntityBounds`) AND `blockGeometry.ts` (per-kind bounds of a BLOCK
 * CHILD, recursing into nested Block/MINSERT children) need this exact same
 * per-kind math — living here (rather than in either of those two files)
 * lets both import it without `bounds.ts` ↔ `blockGeometry.ts` forming an
 * import cycle (`.dependency-cruiser.cjs` `no-circular`, severity error).
 */

import {
  alignedDimProjectedPoints,
  bezierLoopsBbox,
  bulgeSegmentBounds,
  buildSplineBeziers,
  edgeToCurve,
  isReflectedOcs,
  mtextAnchoredBoxCorners,
  mtextAnchoredBoxRings,
  mtextBoxSize as mtextBoxSizeShared,
  mtextLocalCorners,
  type MtextPaintedExtents,
  multileaderDoglegEnd,
  multileaderNodeLastVertex,
  numOrLocal as numOrDim,
  positiveOrLocal as positiveOrDim,
  resolveDimensionStyleFromContext,
  splineLeaderCubics as splineLeaderCubicsShared,
  splineLeaderControlPoints,
  textLocalCorners,
  type BezierLoop,
  type AttdefEntity,
  type DimensionEntity,
  type DimensionStyle,
  type EllipseEntity,
  type HatchEntity,
  type MtextEntity,
  type MultileaderEntity,
  type PolylineVertex,
  type SplineEntity,
  type SplineLeaderCubic,
  type ArrayRectEntity,
  type TextEntity,
  type ViewportEntity,
  type WipeoutEntity,
} from "@dxf-ai-editor/drawing-model";

import type { DimensionStyleTable } from "./blockLookupContext";
import type { Aabb, Vec2 } from "./common";
import { boundsFromPoints, ellipseBoundsRad, rotateByArrayAxisAngle, rotateRad } from "./common";
import {
  multileaderAnnotativeContextCorners,
  multileaderLandingEnd,
  multileaderTextContentCorners,
} from "./multileaderContextBounds";

/**
 * The three ANNOTATIVE-CONTEXT-parameterised MULTILEADER helpers live in
 * `./multileaderContextBounds.ts` (CLAUDE.md file-size split) and are
 * re-exported HERE so every existing importer — `selectLeaf.ts`, `grips.ts`
 * and the package's own tests — keeps its `./leafBounds` path unchanged (the
 * documented barrel-re-export split pattern).
 */
export { multileaderAnnotativeContextCorners, multileaderLandingEnd, multileaderTextContentCorners };

export function ellipseBounds(e: EllipseEntity): Aabb | null {
  return ellipseBoundsRad(
    e.center.x,
    e.center.y,
    e.majorAxisEndpoint.x,
    e.majorAxisEndpoint.y,
    e.ratio,
    e.startParam,
    e.endParam,
  );
}

/**
 * Exact-enough SPLINE bounds: convert to cubic-Bezier segments (honouring
 * rational weights when `rational` is set — the "rational weights" edge
 * case named by the task) via the one-truth `buildSplineBeziers`
 * (`drawing-model/splineMath.ts`), then bound the per-segment CONTROL HULL
 * (p0/cp1/cp2/p3) — a cubic Bezier never leaves the convex hull of its own
 * four control points, so this is a tight, standard bound (the same
 * technique `hatchBoundaryGeometry.ts::bezierLoopsBbox` uses for hatch
 * pattern-line extents), far tighter than bounding the WHOLE curve's
 * defining polygon.
 */
export function splineBounds(e: SplineEntity): Aabb | null {
  // `exactOptionalPropertyTypes: true` requires OMITTING an optional key
  // entirely rather than assigning it `undefined` — build the input via
  // conditional spread instead of ternary-to-undefined per field.
  const beziers = buildSplineBeziers({
    ...(e.fitPoints.length > 0 ? { fitPoints: e.fitPoints } : {}),
    ...(e.startTangent ? { startTangent: e.startTangent } : {}),
    ...(e.endTangent ? { endTangent: e.endTangent } : {}),
    ...(e.controlPoints.length > 0 ? { controlPoints: e.controlPoints } : {}),
    ...(e.knots.length > 0 ? { knots: e.knots } : {}),
    ...(e.rational && e.weights.length > 0 ? { weights: e.weights } : {}),
    degree: e.degree,
  });
  if (beziers.length === 0) {
    const pts = e.controlPoints.length > 0 ? e.controlPoints : e.fitPoints;
    return boundsFromPoints(pts);
  }
  const pts: Vec2[] = [];
  for (const seg of beziers) pts.push(seg.p0, seg.cp1, seg.cp2, seg.p3);
  return boundsFromPoints(pts);
}

/**
 * Bulge-aware polyline bounds shared by LWPOLYLINE-family kinds that
 * `entityModelBounds` does not cover (Revision Cloud is stored on disk as
 * an LWPOLYLINE-derived entity — model.ts `revisionCloudEntitySchema`
 * comment). Reuses the one-truth `bulgeSegmentBounds` (`bulgeArc.ts`) —
 * the SAME arc-extent math `entityModelBounds`'s own LWPOLYLINE case uses,
 * just not exported per-kind from that module.
 */
export function bulgePolylineBounds(
  vertices: readonly PolylineVertex[],
  closed: boolean,
): Aabb | null {
  if (vertices.length === 0) return null;
  let acc: Aabb | null = null;
  const merge = (b: { minX: number; minY: number; maxX: number; maxY: number }): void => {
    if (!Number.isFinite(b.minX) || !Number.isFinite(b.minY)) return;
    acc = acc
      ? {
          minX: Math.min(acc.minX, b.minX),
          minY: Math.min(acc.minY, b.minY),
          maxX: Math.max(acc.maxX, b.maxX),
          maxY: Math.max(acc.maxY, b.maxY),
        }
      : { minX: b.minX, minY: b.minY, maxX: b.maxX, maxY: b.maxY };
  };
  for (let i = 1; i < vertices.length; i++) {
    const v1 = vertices[i - 1]!;
    const v2 = vertices[i]!;
    merge(bulgeSegmentBounds(v1, v2, v1.bulge));
  }
  if (closed && vertices.length >= 2) {
    const last = vertices[vertices.length - 1]!;
    const first = vertices[0]!;
    merge(bulgeSegmentBounds(last, first, last.bulge));
  }
  return acc ?? boundsFromPoints(vertices);
}

/**
 * DIMENSION bounds — every def point (extension lines, dim-line point,
 * third/arc point for Angular/Radius/Diameter) PLUS a conservative pad
 * around the dim-line point for the extension-line overshoot (DIMEXE) and
 * the text label (DIMGAP + text height), mirroring `entityBounds.ts`'s
 * Dimension case. Unlike the legacy string-bag path (which always defaults
 * missing extension points to (0,0)), the typed `extLine1Point` /
 * `extLine2Point` / `thirdPoint` / `arcPoint` fields are genuinely OPTIONAL
 * (absent for subtypes that do not carry them, e.g. Radius/Diameter have no
 * extension lines) — this implementation only includes points that are
 * actually present, which is strictly more correct.
 */
// The ALIGNED-dimension (dimensionType 1) rendered-endpoint projection (`alignedDimProjectedPoints`)
// is imported from `@dxf-ai-editor/drawing-model` (see the import block above) — `drawing-model`'s
// own `wcsBoundsLeaf.ts::dimensionLocal` needs the IDENTICAL formula for its own bounds helper, and
// `cad-core` DEPENDS ON `drawing-model` (never the reverse), so defining it once there and importing
// it here is the correct direction (dup:check convergence — mirrors the established
// `ellipsePointAtDecomposed`/`mtextBoxSize`/`Vec3` precedent), not a re-derivation.

/**
 * The candidate point set a DIMENSION's AABB frames — every present definition
 * point (extension lines, dim-line point, third/arc point) + text midpoint,
 * PLUS a conservative pad around the dim-line point for the extension-line
 * overshoot (DIMEXE) and the text label (DIMGAP + text height), PLUS (for the
 * ALIGNED subtype) the render-projected dimension-line endpoints (Codex #467
 * P2). Shared by {@link dimensionBounds} AND `select.ts`'s Dimension case
 * (Codex #467 P2 "Dimension select geometry misses padding") — exposing this
 * exact padded/projected hull as the select `points` makes a window-selection
 * "all points inside" test agree with the bounds, closing the false-accept
 * where a window contained the raw definition points but excluded the
 * padded/projected overshoot.
 *
 * NAMED-DIMSTYLE TIER (Codex PR #784 round 3 P1 — supersedes the round-22 "DOCUMENTED GAP" note
 * this doc previously carried): every DIMVAR below now resolves through THREE tiers —
 * per-entity `styleOverrides` (the DSTYLE XData override) → the entity's NAMED DIMSTYLE-table row
 * (`dimStyles`, the document's `styleTables.dimensionStyles` keyed by name, resolved with the
 * renderer's own `"Standard"` fallback, via the shared `resolveDimensionStyleFromContext`) →
 * AutoCAD's documented hardcoded default. Before this fix the middle tier did not exist: a block
 * definition whose Dimension child uses a named style with a large DIMEXE / DIMTXT / DIMGAP /
 * DIMASZ was bounded with the hardcoded defaults, so the rendered dimension geometry
 * (`scene3d/adapters/typed/dimensionFromEntity.ts`, which resolves the row from
 * `ctx.dimensionStyles`) fell OUTSIDE the INSERT metadata box that
 * `blockExtentsCache.ts` → `resolveBlockInsertBoundsOrNull` writes into the entity metadata
 * index — cache-direct culling, fit-to-extents and broad-phase selection then missed it.
 * `dimStyles` is threaded from `BlockLookupContext.dimensionStyles` through
 * `bounds.ts::getEntityBounds`, `selectLeaf.ts`'s Dimension case and
 * `blockDefinitionWalk.ts`'s block-child Dimension arm; ABSENT ⇒ byte-identical to the
 * pre-fix override→default behaviour (never a hard requirement — same "absent context degrades
 * to the prior box" contract `blockDefinitions` itself has).
 *
 * The per-entity tier deliberately keeps FIRST precedence here (unlike
 * `wcsBoundsLeaf.ts::dimensionLocal`, whose round-25 fix dropped it): this function is the
 * one-truth cad-core grips/select/bounds hull, and `apps/web`'s overlap-warning path
 * (`scene/dimensionHelpers.ts::applyDimensionStyleOverrides`,
 * `scene/insertionOverlap.ts::resolveDimensionStyleBounds`) MATERIALISES the effective
 * style values onto `styleOverrides` before calling in — dropping the tier would make that
 * established enrichment a no-op. With that enrichment applied the two tiers agree by
 * construction; where they differ, the override is the more specific authored value.
 * DIMASZ (arrowhead / tick size) joins the pad here for the same reason
 * `wcsBoundsLeaf.ts::dimensionLocal` includes it (round-21: `dimensionGeometry.ts
 * ::emitArrowOrTick` draws a `style.dimasz`-sized marker at every dim-line endpoint, extending
 * up to ~0.5·DIMASZ perpendicular to and past the dim line). Its AutoCAD default (2.5) is
 * strictly below the default `dimgap + dimtxt` (0.625 + 2.5) term, so a default-valued
 * dimension's pad is UNCHANGED by its inclusion.
 */
export function dimensionBoundsPoints(e: DimensionEntity, dimStyles?: DimensionStyleTable): Vec2[] {
  const pts: Vec2[] = [e.defPoint];
  if (e.extLine1Point) pts.push(e.extLine1Point);
  if (e.extLine2Point) pts.push(e.extLine2Point);
  if (e.thirdPoint) pts.push(e.thirdPoint);
  if (e.arcPoint) pts.push(e.arcPoint);

  // ONE truth for "which row does this DIMENSION's `styleName` resolve to" — drawing-model's
  // `resolveDimensionStyleFromContext` (own-property-safe named lookup + the renderer's
  // `"Standard"` fallback), the SAME resolver `wcsBounds.ts`/`wcsBoundsBlock.ts` use; never a
  // second re-derived lookup rule here.
  // `e.styleName` verbatim — the SAME call shape `wcsBounds.ts`'s own Dimension
  // case uses. No local `?? "Standard"` pre-step: the shared resolver ALREADY
  // falls back to the table's own `"Standard"` row for any name it cannot find
  // (including `""`), so a second fallback here would be a dead, untestable
  // duplicate of that rule (`styleName` carries a schema `.default("Standard")`).
  const row: DimensionStyle | undefined = resolveDimensionStyleFromContext(e.styleName, dimStyles);
  // DIMEXE/DIMEXO/DIMGAP accept ANY finite value; DIMTXT/DIMASZ are POSITIVE-clamped — the exact
  // finite/positive guards `dimensionHelpers.ts::dimensionStyleRowToValues` applies to the same
  // fields, so a malformed (0 / negative / non-finite) table value falls through to the next tier
  // here exactly as it does for the render, instead of silently zeroing the pad.
  const dimexe = e.styleOverrides.dimexe ?? numOrDim(row?.DIMEXE, 1.25);
  const dimexo = e.styleOverrides.dimexo ?? numOrDim(row?.DIMEXO, 0.625);
  const dimtxt = e.styleOverrides.dimtxt ?? (e.textHeight > 0 ? e.textHeight : positiveOrDim(row?.DIMTXT, 2.5));
  const dimgap = e.styleOverrides.dimgap ?? numOrDim(row?.DIMGAP, 0.625);
  const dimasz = e.styleOverrides.dimasz ?? positiveOrDim(row?.DIMASZ, 2.5);

  pts.push(...alignedDimProjectedPoints(e, dimexo, dimexe));

  const pad = Math.max(Math.abs(dimexe), Math.abs(dimgap) + Math.abs(dimtxt), Math.abs(dimasz));
  if (pad > 0) {
    pts.push({ x: e.defPoint.x + pad, y: e.defPoint.y + pad });
    pts.push({ x: e.defPoint.x - pad, y: e.defPoint.y - pad });
    if (e.extLine1Point) {
      const sign = Math.sign(e.defPoint.y - e.extLine1Point.y) || 1;
      pts.push({ x: e.extLine1Point.x, y: e.extLine1Point.y + sign * pad });
    }
    if (e.extLine2Point) {
      const sign = Math.sign(e.defPoint.y - e.extLine2Point.y) || 1;
      pts.push({ x: e.extLine2Point.x, y: e.extLine2Point.y + sign * pad });
    }
  }
  // Text midpoint is always present (unlike the extension points) — frame it too.
  pts.push(e.textMidpoint);
  return pts;
}

/**
 * `dimStyles` — the document's name-keyed DIMSTYLE table (optional); see
 * {@link dimensionBoundsPoints}'s NAMED-DIMSTYLE TIER note for the resolution order and the
 * INSERT-metadata gap it closes.
 */
export function dimensionBounds(e: DimensionEntity, dimStyles?: DimensionStyleTable): Aabb | null {
  return boundsFromPoints(dimensionBoundsPoints(e, dimStyles));
}

/**
 * The DOGLEG endpoint for one multileader node, given its LAST leader-line
 * vertex — the one-truth shared by `multileaderBounds` (below), `grips.ts
 * ::multileaderGrips`'s fallback-dogleg grip, and `select.ts`'s Multileader
 * case (Codex #467 P2 + round-2 "Emit the fallback multileader dogleg grip"
 * / "Include rendered MLEADER dogleg segments in select geometry"). Mirrors
 * the renderer (`scene3d/dimensions/multileaderToPrimitives.ts`): direction
 * is `node.doglegVector` (normalised) when the node carries one, else a
 * HORIZONTAL fallback signed toward `textLocation` — length is the node's
 * own `doglegLength` override, else the entity-level `doglegLength`. Returns
 * `null` when landing/dogleg is disabled or the node has no leader-line
 * vertex to jog from (nothing to anchor the dogleg to).
 */
// Imported from `@dxf-ai-editor/drawing-model` (see the import block above) —
// `drawing-model`'s own `wcsBoundsLeaf.ts::multileaderDoglegEnd` needs the IDENTICAL formula for
// its own bounds helper, and `cad-core` DEPENDS ON `drawing-model` (never the reverse), so
// defining it once there and importing it here is the correct direction (dup:check convergence —
// mirrors the established `ellipsePointAtDecomposed`/`mtextBoxSize`/`Vec3` precedent), not a
// re-derivation. Re-exported below so `grips.ts`/`select.ts` keep importing it from
// `./leafBounds` (no call-site churn).
export { multileaderDoglegEnd };

/**
 * The effective LAST leader-line vertex a node's dogleg jogs off of — the
 * one-truth shared by `multileaderBounds`, `grips.ts::multileaderGrips` and
 * `select.ts`'s Multileader case (Codex #467 P2 "MLEADER dogleg ignores the
 * implicit landing point on a single-vertex leader"). Both live renderers
 * (`scene/renderMultileader.tsx` and `scene3d/dimensions/
 * multileaderToPrimitives.ts`) SYNTHESIZE an implicit second vertex at
 * `textLocation` when a leader line carries only ONE vertex (the arrow point,
 * the common MLEADER-command case), then jog the dogleg off THAT synthesized
 * landing point — so the true jog anchor is `textLocation`, not the lone arrow
 * vertex. Mirrors the renderer's per-node rule: the dogleg is drawn only on the
 * node's LAST leader line (`li === nodeLines.length - 1`), and every leader
 * line always has ≥1 vertex (`mleaderLeaderLineSchema.vertices.min(1)`).
 * Returns `undefined` when the node has no leader line to anchor to.
 */
// Imported from `@dxf-ai-editor/drawing-model` (same rationale as `multileaderDoglegEnd` above)
// and re-exported so `grips.ts`/`select.ts` keep importing it from `./leafBounds`.
export { multileaderNodeLastVertex };

/**
 * The cubic-Bézier CONTROL points the renderer bows a SPLINE-type
 * (`leaderLineType === 2`) leader line OUT to — one cubic per consecutive
 * vertex pair, tangents drawn from the Catmull-Rom neighbourhood (interior:
 * `(P[i+1] − P[i−1]) / 6`; reflective at each open end:
 * `(P[end] − P[end∓1]) / 3`). Mirrors `scene/renderMultileader.tsx
 * ::catmullRomToCubicBezierPath` (and the PDF `multileaderConverter.ts` twin)
 * EXACTLY. A leader line with FEWER than 3 vertices is drawn STRAIGHT
 * (`catmullRomToCubicBezierPath`'s `n <= 2` early return: an `M…L…` line, no
 * curve — including the single-vertex synthesized-landing case, which yields
 * exactly 2 vertices), so `[]` is returned there (the raw-vertex hull is
 * already exact). For ≥3 vertices the interior control points can bow OUTSIDE
 * the raw-vertex AABB, which the bounds/select paths must include (Codex #467
 * P2 "Account for spline MLEADER leader curves").
 *
 * `SplineLeaderCubic`/`splineLeaderCubics`/`splineLeaderControlPoints` are imported from
 * `@dxf-ai-editor/drawing-model` (see the import block above) — `drawing-model`'s own
 * `wcsBoundsMath.ts::splineLeaderCubics` needs the IDENTICAL Catmull-Rom formula for its own
 * bounds helper, and `cad-core` DEPENDS ON `drawing-model` (never the reverse), so defining it
 * once there and importing it here is the correct direction (dup:check convergence — mirrors the
 * established `ellipsePointAtDecomposed`/`mtextBoxSize`/`Vec3` precedent), not a re-derivation.
 * Re-exported below so `splineLeaderPolyline` (this file) and any external `./leafBounds`
 * importer keep the same names/paths.
 */
export { splineLeaderControlPoints, type SplineLeaderCubic };

/**
 * On-curve tessellation of a SPLINE leader line into `stepsPerCubic`
 * sub-segments per cubic — the polyline `select.ts`'s Multileader case
 * rasterises crossing tests against, so a crossing line hits the real curve
 * BULGE (between vertices) the renderer draws, not just the straight
 * raw-vertex chords. Empty for a straight (<3-vertex) leader (the caller keeps
 * its chord segments). Same Catmull-Rom cubics as
 * {@link splineLeaderControlPoints} — one truth.
 */
export function splineLeaderPolyline(vertices: readonly Vec2[], stepsPerCubic = 8): Vec2[] {
  const cubics = splineLeaderCubicsShared(vertices);
  if (cubics.length === 0) return [];
  const pts: Vec2[] = [cubics[0]!.p0];
  for (const c of cubics) {
    for (let i = 1; i <= stepsPerCubic; i++) {
      const t = i / stepsPerCubic;
      const u = 1 - t;
      const b0 = u * u * u;
      const b1 = 3 * u * u * t;
      const b2 = 3 * u * t * t;
      const b3 = t * t * t;
      pts.push({
        x: b0 * c.p0.x + b1 * c.c1.x + b2 * c.c2.x + b3 * c.p3.x,
        y: b0 * c.p0.y + b1 * c.c1.y + b2 * c.c2.y + b3 * c.p3.y,
      });
    }
  }
  return pts;
}

/**
 * MULTILEADER bounds — every leader-line vertex (arrow through landing)
 * across every root/node/line, the text/block content location, the DOGLEG
 * segment endpoint when landing + dogleg are enabled (Codex #467 P2 —
 * {@link multileaderDoglegEnd}), PLUS (Codex #467 round-2 P2 "Bound
 * multileader text content, not just its anchor") the embedded MTEXT
 * content-box extent when `contentType === 2` — see
 * {@link multileaderTextContentCorners} — PLUS (Codex #467 P2 "Account for
 * spline MLEADER leader curves") the Catmull-Rom control hull of every SPLINE
 * (`leaderLineType === 2`) leader line, whose curve bows past the raw vertices
 * ({@link splineLeaderControlPoints}) — PLUS (Codex PR #784 round 9 P2) the
 * LANDING endpoint past each dogleg ({@link multileaderLandingEnd}) and the
 * whole per-ANNOTATIVE-CONTEXT union
 * ({@link multileaderAnnotativeContextCorners}).
 *
 * The landing endpoint used to be documented as needing no point of its own
 * ("it lies ON the dogleg-end↔textLocation line") — true only while
 * `landingGap ≤ |textLocation − doglegEnd|`. A LONGER gap sends
 * `textLocation − unit(textLocation − doglegEnd)·landingGap` PAST the dogleg
 * end, outside that pair, exactly the hole `drawing-model`'s own two MLEADER
 * walks closed in their round-25 Fix 1 / round-26 Fix 3; it is unioned here
 * for the master representation and, at each context's own `landingGap`, by
 * the per-context union.
 */
export function multileaderBounds(e: MultileaderEntity): Aabb | null {
  const pts: Vec2[] = [e.textLocation];
  if (e.basePoint) pts.push(e.basePoint);
  if (e.contentType === 2) pts.push(...multileaderTextContentCorners(e));
  const spline = e.leaderLineType === 2;
  for (const root of e.leaderRoots) {
    for (const node of root.nodes) {
      for (const line of node.lines) {
        for (const v of line.vertices) pts.push(v);
        if (spline) pts.push(...splineLeaderControlPoints(line.vertices));
      }
      if (node.lastLeaderPoint) pts.push(node.lastLeaderPoint);

      // The dogleg jogs off the LAST line's (synthesized) last vertex —
      // `textLocation` when that line has a single vertex (Codex #467 P2).
      const doglegEnd = multileaderDoglegEnd(e, node, multileaderNodeLastVertex(e, node));
      if (doglegEnd) {
        pts.push(doglegEnd);
        // …and the landing endpoint past it (see this function's doc: only
        // INTERIOR to the {doglegEnd, textLocation} pair while `landingGap`
        // stays under the dogleg-to-text distance).
        pts.push(multileaderLandingEnd(e, doglegEnd));
      }
    }
  }
  // The rendered arrowhead glyph at every leader-line tip (Codex PR #784 round 8 P2 "Include
  // MLEADER arrowheads in block bounds"): the arrow mesh extends ±0.5·size PERPENDICULAR to the
  // leader and up to 1·size BEHIND the tip, so a large arrowhead reaches outside the raw
  // leader/dogleg/content hull above — bounding by the tip alone left it outside the INSERT
  // metadata box, dropping visible geometry from cache-direct culling and picking.
  pts.push(...multileaderArrowCorners(e));
  // Every ANNOTATIVE representation's own dogleg / landing / text-content
  // footprint (Codex PR #784 round 9 P2 "Include annotative MLEADER contexts
  // in block bounds"): the renderer draws the ACTIVE context's `doglegLength`
  // / `landingGap` / `textHeight` / `textRotation`, and a document-global
  // bounds cannot know which scale is active — see
  // {@link multileaderAnnotativeContextCorners}'s doc for the full
  // field-by-field derivation off the render adapter (and for why the
  // per-context ARROW size is already covered by `multileaderArrowCorners`
  // above rather than re-walked here).
  pts.push(...multileaderAnnotativeContextCorners(e));
  return boundsFromPoints(pts);
}

/**
 * The unit-size (`size === 1`) local AABB that CONTAINS every one of the renderer's 10 stock
 * arrowhead prototypes. The renderer's prototype geometry is GPU `THREE.BufferGeometry` in
 * `apps/web/…/scene3d/dimensions/arrowMarkerMesh.ts`, which cad-core cannot import
 * (`.dependency-cruiser.cjs` `cad-core-no-back-edges`) and which is not reusable as a coordinate
 * set anyway — so this is the analytic UNION-AABB of those prototypes, derived from their literal
 * vertices and pinned by {@link multileaderArrowCorners}'s containment test against the SAME
 * literal prototype vertices the renderer draws:
 *
 *   - body length: `closed-filled`/`closed`/`open`/`open30`/`dot` all extend to local x = −1
 *     (tip at the origin, body back along −X — `arrowMarkerMesh.ts`'s coordinate convention);
 *   - half-width: `closed`/`open`/`oblique`/`dot` reach local y = ±0.5;
 *   - forward overhang: only `_ARCHTICK` crosses the tip, its 45° slash reaching local
 *     x ≈ +0.492 / y ≈ ±0.492 — covered by the +0.5 upper x bound.
 *
 * A conservative (never-under-report) box: it over-covers `open30`/`oblique`/`arch-tick`, matching
 * this package's `ALWAYS_INCLUDE`-style bounds convention. If the renderer ever flattens a REAL
 * arrowhead BLOCK (an INSERT of a user block) instead of a stock prototype, this bound — and the
 * `_NONE`-only skip in {@link multileaderArrowCorners} — must be revisited; today the renderer's
 * `dimblkToVariant` collapses EVERY non-stock arrowhead-block name to `closed-filled`, so no user
 * block-INSERT arrow geometry is ever emitted and the stock box covers every case it can draw.
 */
export const ARROW_PROTOTYPE_LOCAL_EXTENT = { minX: -1, maxX: 0.5, minY: -0.5, maxY: 0.5 } as const;

/**
 * The 4 transformed corners of every arrowhead the renderer emits for `e` — one arrow per leader
 * line, at its FIRST vertex (`tip`), oriented along `unit(tip − secondVertex)` and scaled by the
 * arrowhead size, exactly as `multileaderGeometry.ts::buildMultileaderGeometry` places it and
 * `arrowInstanceMesh.ts::buildArrowInstanceMeshes` materialises it (translate(tip) · rotateZ(
 * atan2(dir)) · scale(size) applied to {@link ARROW_PROTOTYPE_LOCAL_EXTENT}). Empty when the
 * leader draws no line (`leaderLineType === 0`) or the arrow size is non-positive.
 *
 * Size is the MAX over the master `arrowheadSize` and every annotative context's `arrowheadSize`
 * (`mleaderContextDataSchema` — the renderer uses `annoCtx.arrowheadSize` verbatim, no scale-factor
 * multiply): a viewport-agnostic document-global bound cannot know which scale is active, so it
 * covers the largest arrow drawn at any scale (the same union-every-context discipline
 * `mtextLocalCorners`/`textLocalCorners` use). The per-line arrowhead-block resolution
 * (`line.arrowheadBlock || e.arrowheadBlock`, `_NONE` ⇒ no arrow) mirrors the renderer's
 * `firstArrow = line.arrowheadBlock || arrowheadBlockFallback` + its `firstArrow !== "_NONE"` gate.
 *
 * Exported so `select.ts`'s Multileader case can reserve the SAME footprint (one truth) — its
 * annotative-select extension is a separate slice, like the other select-vs-bounds gaps.
 */
export function multileaderArrowCorners(e: MultileaderEntity): Vec2[] {
  // `leaderLineType === 0` ⇒ no leader line is drawn, so no arrowhead either (the renderer gates
  // the arrow on `raw.leaderType !== 0`).
  if (e.leaderLineType === 0) return [];
  let size = e.arrowheadSize;
  for (const ctx of e.annotativeContexts ?? []) size = Math.max(size, ctx.arrowheadSize);
  if (!(size > 0) || !Number.isFinite(size)) return [];
  const ext = ARROW_PROTOTYPE_LOCAL_EXTENT;
  const localCorners: readonly Vec2[] = [
    { x: ext.minX, y: ext.minY },
    { x: ext.maxX, y: ext.minY },
    { x: ext.maxX, y: ext.maxY },
    { x: ext.minX, y: ext.maxY },
  ];
  const out: Vec2[] = [];
  for (const root of e.leaderRoots) {
    for (const node of root.nodes) {
      for (const line of node.lines) {
        // The renderer resolves the per-line arrowhead block as `line.arrowheadBlock ||
        // entity.arrowheadBlock`, and skips the arrow only for the exact `_NONE` block.
        const block = line.arrowheadBlock || e.arrowheadBlock;
        if (block === "_NONE") continue;
        const v0 = line.vertices[0];
        if (!v0) continue;
        // Second vertex is the synthesized landing (`textLocation`) when the line has a lone
        // vertex — the SAME single-vertex synthesis the renderer applies before deriving the
        // arrow direction.
        const v1 = line.vertices[1] ?? e.textLocation;
        // `rot = atan2(dir.y, dir.x)` with `dir = v0 − v1`; normalization is irrelevant to the
        // angle, and a degenerate `v0 === v1` yields `atan2(0, 0) === 0`, matching the renderer's
        // `unitVec2` zero-guard (which returns `{1, 0}` ⇒ angle 0).
        const rot = Math.atan2(v0.y - v1.y, v0.x - v1.x);
        for (const c of localCorners) {
          const p = rotateRad(c.x * size, c.y * size, rot);
          out.push({ x: v0.x + p.x, y: v0.y + p.y });
        }
      }
    }
  }
  return out;
}

export function wipeoutBounds(e: WipeoutEntity): Aabb | null {
  return boundsFromPoints(e.vertices);
}

/**
 * Per-loop sampled boundary points of a HATCH — one point array per boundary
 * path — via the one-truth `edgeToCurve`
 * (`drawing-model/hatchBoundaryGeometry.ts`), the SAME parametric edge sampling
 * `select.ts`'s Hatch case rasterises against (a `line` edge → 1 segment; an
 * `arc`/`ellipticArc` edge → 16). Reused by both {@link hatchBounds} AND
 * `select.ts` so a hatch's bounds and its select geometry can never drift
 * (Codex #467 P2: the prior `entityModelBounds` hatch box used a coarse
 * `center ± radius` viewport-cull span that inflated a QUARTER-arc boundary
 * edge to the full circle — this samples the real swept span instead).
 *
 * The first sample of every edge after the first in a loop is dropped (it
 * coincides with the previous edge's last sample in a well-formed closed loop),
 * matching select's own de-dup.
 */
export function hatchLoopPoints(e: HatchEntity): Vec2[][] {
  const loops: Vec2[][] = [];
  for (const path of e.boundaryPaths) {
    const loopPoints: Vec2[] = [];
    for (const edge of path.edges) {
      const curve = edgeToCurve(edge);
      if (!curve) continue;
      const sampled = curve.toPolyline(edge.edgeType === "line" ? 1 : 16);
      const start = loopPoints.length > 0 ? 1 : 0;
      for (let i = start; i < sampled.length; i++) loopPoints.push(sampled[i]!);
    }
    loops.push(loopPoints);
  }
  return loops;
}

/**
 * HATCH bounds — the AABB of the real swept boundary geometry, bounded from
 * the EXACT curve extent, NOT a fixed sample count (Codex #467 P2 "Bound HATCH
 * curves from extrema, not fixed samples"). Each edge is converted to its
 * one-truth cubic-Bézier decomposition (`edgeToCurve().toBeziers()` — the SAME
 * ≤90°-span exact cubics both render pipes draw) and the union is taken over
 * every segment's CONTROL HULL (`bezierLoopsBbox`, reused verbatim from
 * `hatchBoundaryGeometry.ts` — the identical hull the PDF pattern-line extent
 * uses). A cubic Bézier never leaves its own {p0,c1,c2,p3} convex hull, so this
 * is a tight, ANALYTIC bound that always contains the true rendered curve —
 * unlike the prior 16-sample-per-edge polyline ({@link hatchLoopPoints}, still
 * used by `select.ts`'s crossing rasterisation), whose extremum can fall
 * BETWEEN two samples: e.g. a 10°→100° arc of radius 100 never samples exactly
 * 90°, so its `maxY` came out BELOW the real top of the curve. `hatchLoopPoints`
 * remains the select-geometry tessellation (a crossing hit-test wants on-curve
 * points, and every sample lies inside this hull, so `select points ⊆ bounds`
 * still holds).
 */
export function hatchBounds(e: HatchEntity): Aabb | null {
  const loops: BezierLoop[] = [];
  for (const path of e.boundaryPaths) {
    for (const edge of path.edges) {
      const curve = edgeToCurve(edge);
      if (!curve) continue;
      loops.push(curve.toBeziers());
    }
  }
  const bbox = bezierLoopsBbox(loops);
  return bbox ? { minX: bbox.minX, minY: bbox.minY, maxX: bbox.maxX, maxY: bbox.maxY } : null;
}

/**
 * RECTANGULAR ASSOCIATIVE ARRAY bounds — the AABB of the rotated grid's
 * anchor-point extent (base point through the far corner at
 * `((columnCount-1)·columnSpacing, (rowCount-1)·rowSpacing)`, rotated by
 * `axisAngle`), matching the item-position semantics `entityBounds.ts`'s
 * Rectangular Associative Array case uses (without that case's arbitrary
 * fixed 36×22 "icon" pad — the typed model carries no such synthetic
 * constant, and item geometry itself is a separate `sourceEntityIds`
 * concern out of scope for a single-entity bounds query).
 *
 * `axisAngle`'s UNIT depends on `e.associative` (RADIANS for a real imported
 * associative array, DEGREES for a bare non-associative block-ref grid) —
 * see {@link rotateByArrayAxisAngle}'s doc for the full architecture note
 * (Codex #467 round-2 P2 "Preserve degree-based ArrayRect angles"; round-1's
 * P2 fix used `rotateRad` UNCONDITIONALLY, correct only for the associative
 * case).
 */
export function arrayRectBounds(e: ArrayRectEntity): Aabb | null {
  const cols = Math.max(1, Math.round(e.columnCount));
  const rows = Math.max(1, Math.round(e.rowCount));
  const colTotal = (cols - 1) * e.columnSpacing;
  const rowTotal = (rows - 1) * e.rowSpacing;
  const corners: Array<[number, number]> = [
    [0, 0],
    [colTotal, 0],
    [0, rowTotal],
    [colTotal, rowTotal],
  ];
  const pts: Vec2[] = corners.map(([lx, ly]) => {
    const r = rotateByArrayAxisAngle(lx, ly, e);
    return { x: e.basePoint.x + r.x, y: e.basePoint.y + r.y };
  });
  return boundsFromPoints(pts);
}

/** VIEWPORT bounds — the paper-space frame rectangle (`center ± width/height / 2`). */
export function viewportBounds(e: ViewportEntity): Aabb | null {
  const halfW = e.width / 2;
  const halfH = e.height / 2;
  return {
    minX: e.center.x - halfW,
    minY: e.center.y - halfH,
    maxX: e.center.x + halfW,
    maxY: e.center.y + halfH,
  };
}

/**
 * Clamp a DXF group-71 MTEXT/ATTDEF attachment-point code to the valid 1..9
 * range, defaulting to 1 (Top-Left) for anything else — mirrors the clamp
 * `scene3d/adapters/typed/mtextFromEntity.ts` applies before rendering.
 */
function clampAttachmentPoint(raw: number | undefined): number {
  const v = raw ?? 1;
  return Number.isFinite(v) && v >= 1 && v <= 9 ? Math.round(v) : 1;
}

/**
 * The 4 corners (WCS, already rotated + translated) of an anchored
 * `width × height` box, per the DXF group-71 ATTACHMENT POINT convention
 * (1..9 — Top/Middle/Bottom row × Left/Center/Right column; Autodesk DXF
 * reference). `insertion` is placed AT the named anchor of the box (e.g.
 * attachment 1 ⇒ insertion is the box's TOP-LEFT corner; 5 ⇒ insertion is
 * the box's CENTER), the box is built in that LOCAL (unrotated) frame, then
 * rotated by `rotationDeg` (CCW, DXF group-50 convention) and translated to
 * `insertion` — shared by MTEXT bounds/select geometry so both use the
 * exact same corner set (one truth; see `select.ts`'s MText case).
 *
 * `extents` is what the MTEXT box overhangs its upright layout column by —
 * the `\\Q` oblique enlargement plus the left side bearing, both ALREADY
 * inside `width` — PLUS the painted y-interval's two ends (`drawing-model`'s
 * {@link MtextPaintedExtents}, straight off {@link mtextBoxSize}). The
 * attachment anchors the UPRIGHT column `width − overhangLeft −
 * overhangRight` and each side is then pushed out by its own extent
 * (@codex PR #794 round 3 P2, round 10 P2 finding B). Omitted ⇒ zero both
 * sides and a mesh rectangle of `x ∈ [0, width]`, `y ∈ [0, height]` ⇒ the
 * group-42/43 convention.
 *
 * # It bounds the SAME two rectangles the bounds facet does
 *
 * The live glyph mesh IGNORES group 71: `sceneTextMeshFactories.ts` puts
 * line 0's baseline at the mesh origin and `mtextGeometry.ts` sets that
 * origin to the insertion point, so every MTEXT paints BOTTOM-LEFT attached
 * on line 0's baseline whatever the attachment says (#801). A box built on
 * the group-71 rectangle ALONE therefore rejects painted glyphs: a default
 * top-left MTEXT at height 2.5 is semantically `y ∈ [-3.225, 0]` while the
 * mesh paints `y ∈ [-0.725, +2.5]`, overlapping on `[-0.725, 0]` — about
 * three quarters of the VISIBLE glyph area would be outside it.
 *
 * So this is `drawing-model`'s `mtextAnchoredBoxCorners` VERBATIM (imported,
 * not re-derived — the same dependency direction and one-truth convergence
 * `mtextBoxSize` / `textLocalCorners` / `multileaderDoglegEnd` already use),
 * which builds both rectangles in ONE local frame and anchors + rotates
 * their HULL once. That hull is what a BOUNDING BOX is; the EXACT select
 * test needs the two rectangles APART and takes {@link anchoredBoxRings}
 * instead (@codex PR #794 round 11 P2, finding 2). #801 — the mesh honouring
 * group 71 — collapses the two into one for both facets at once.
 *
 * The attachment CLAMP stays here: {@link clampAttachmentPoint} rounds a
 * finite in-range code to an integer (mirroring
 * `scene3d/adapters/typed/mtextFromEntity.ts`) before the shared builder's
 * own 1..9 guard sees it, so a fractional group 71 keeps selecting at the
 * grid cell the renderer draws at rather than falling into the builder's
 * right-column arm.
 */
export function anchoredBoxCorners(
  insertion: Vec2,
  width: number,
  height: number,
  attachmentPoint: number | undefined,
  rotationDeg: number,
  extents?: MtextPaintedExtents,
  mirrored = false,
): Vec2[] {
  return mtextAnchoredBoxCorners(
    insertion,
    width,
    height,
    clampAttachmentPoint(attachmentPoint),
    rotationDeg,
    extents,
    mirrored,
  );
}

/**
 * The SAME two rectangles {@link anchoredBoxCorners} bounds, kept APART as two closed rings —
 * `drawing-model`'s `mtextAnchoredBoxRings` with this file's attachment clamp, for the same
 * one-truth reason {@link anchoredBoxCorners} delegates.
 *
 * A BOUNDING BOX may be the hull of the two; an EXACT select test may not. Two rectangles need
 * not cover their own hull — at a top-right attachment the group-71 rectangle sits left/below
 * the insertion point and the mesh rectangle right/above it, leaving large top-left and
 * bottom-right regions inside the hull that belong to NEITHER — so closing the hull's 4 points
 * into one ring would select the MTEXT from a crossing window through empty space (@codex PR
 * #794 round 11 P2, finding 2). `selectLeaf.ts`'s MText arm therefore emits one ring per
 * rectangle; `mtextBounds` keeps the hull, which is what a bounding box IS.
 *
 * The rings also differ from the hull in ONE number, the same principle one axis over: the mesh
 * ring's RIGHT edge is `selectPaintedRight` — the contents walk clamped by group 42 where the
 * file carries one — while {@link anchoredBoxCorners} unions the unclamped walk. The walk bounds
 * every glyph by `MAX_GLYPH_ADVANCE` (1.22 heights against a bundled-prose average near 0.5), so
 * a 36-glyph authoritative-42 MTEXT at the 2.5 nominal walks to 112.785 where the atlases paint
 * 26-31 and group 42 ends at 50: over-reporting the PREFILTER costs a wasted exact test, but 62
 * units of empty strip in the EXACT ring select text that is not there (@codex PR #794 round 13
 * P2). BOUNDS ⊇ SELECT, always. `drawing-model`'s `mtextAnchoredBoxRings` doc carries the
 * derivation, the declared cost of the tighter ring and why the Y axis is NOT clamped the same
 * way; this wrapper only adds the attachment clamp.
 */
export function anchoredBoxRings(
  insertion: Vec2,
  width: number,
  height: number,
  attachmentPoint: number | undefined,
  rotationDeg: number,
  extents?: MtextPaintedExtents,
  mirrored = false,
): Vec2[][] {
  return mtextAnchoredBoxRings(
    insertion,
    width,
    height,
    clampAttachmentPoint(attachmentPoint),
    rotationDeg,
    extents,
    mirrored,
  );
}

/**
 * Is this MTEXT drawn with a REFLECTED (left-handed) glyph frame? `drawing-model`'s one-truth
 * `isReflectedOcs` over the entity's own group-210 normal — the SAME bit
 * `scene3d/adapters/typed/mtextFromEntity.ts` turns into the renderer's `mesh.scale.set(1, -1,
 * 1)`, so the painted rectangle the box builders union in flips exactly when the mesh does
 * (@codex PR #794 round 11 P2, finding 3).
 */
export function mtextIsMirrored(e: MtextEntity): boolean {
  return isReflectedOcs(e.extrusion);
}

/**
 * MTEXT bounds — a real, non-degenerate text-box AABB (Codex #467 P2:
 * previously collapsed to `viewportMembership.ts::mtextBounds`'s
 * insertion-point-only box). Width/height priority (most-authoritative
 * first):
 *
 *   1. `horizontalWidth` / `verticalHeight` (DXF groups 42/43) — AutoCAD's
 *      OWN read-only COMPUTED box dimensions, present whenever the file was
 *      saved by AutoCAD/a conformant writer. Exact, not an approximation.
 *   2. A content-length estimate (`nominalHeight` × longest-paragraph
 *      character count × 0.6, widened by the inline `\H` / `\W` / `\T`
 *      scales and the `\Q` shear the renderer honours), mirroring the
 *      renderer's own heuristic
 *      (`scene3d/adapters/geom/mtextGeometry.ts`) for width; the painted
 *      block span (`mtextBlockSpan`) for height. The shared
 *      `drawing-model::mtextBoxSize` this file re-exports owns both.
 *
 * `referenceWidth` (DXF group 41, the wrap/reference rectangle) is NOT a
 * rung and used to be listed here as one. It is the column AutoCAD would
 * wrap at, and nothing in this repo wraps — the renderer breaks lines only
 * on `\P` / `\n`, and the `mtext` SceneGeometry is not even handed the
 * column — so it describes no painted geometry. Used verbatim it
 * over-reported catastrophically (group 41 = 210 713 on a 10-character
 * title-block label blanked a 40 MB drawing); used as a `Math.min` cap it
 * under-reported a long paragraph with a tight column, dropping painted
 * glyphs out of culling and selection (@codex PR #794 rounds 1 and 5). It
 * now plays no role at either end. Full derivation:
 * `drawing-model/src/wcsBoundsText.ts::mtextEstimatedWidth`.
 *
 * The box is anchored at `insertionPoint` per the group-71 attachment point
 * and rotated by the EFFECTIVE text angle — see {@link effectiveMtextAngleDeg}.
 */
/**
 * Is the MTEXT X-axis direction vector (group 11/21/31) a GENUINELY custom
 * direction, or the schema default `{1,0,0}` (equivalently absent)?
 * `mtextEntitySchema.xAxisDirection` DEFAULTS to `{x:1,y:0,z:0}` (never
 * `undefined` on the typed model), so a rotation-driven MTEXT (angle in
 * group 50, no custom group 11) arrives with `{1,0,0}` — NOT a custom
 * direction.
 *
 * Local (cad-core-only) re-implementation of `command-engine/
 * geometryTransforms.ts::isCustomXAxisDirection` — cad-core cannot import
 * `command-engine` (`.dependency-cruiser.cjs` `cad-core-no-back-edges`), so
 * this tiny pure predicate is duplicated here rather than shared. Any drift
 * between the two copies would silently re-open Codex #467 round-2's
 * "Honor MTEXT xAxisDirection in bounds" finding — kept intentionally
 * literal (no branching beyond the default-vector compare) to keep the two
 * copies trivially diffable against each other.
 */
function isCustomMtextXAxisDirection(dir: { x: number; y: number; z?: number }): boolean {
  return !(dir.x === 1 && dir.y === 0 && (dir.z ?? 0) === 0);
}

/**
 * The EFFECTIVE on-screen MTEXT angle (degrees CCW) a bounds/select query
 * must use — mirrors `command-engine/geometryTransforms.ts
 * ::effectiveMTextAngleDeg` (same duplication rationale as
 * {@link isCustomMtextXAxisDirection}). Per the DXF reference + ezdxf
 * `MText.get_rotation()`, the X-axis DIRECTION VECTOR (group 11/21/31) takes
 * PRECEDENCE over the `rotation` attribute (group 50) **when it is custom**:
 * the renderer (`mtextFromEntity.ts` / `buildMTextPreview`) places the glyph
 * run at this angle, not the raw `rotation` scalar — an imported MTEXT with
 * rotation=0 but a genuinely custom group-11 direction renders (and must
 * therefore be BOUNDED/SELECTED) at the vector's angle, not 0°.
 */
function effectiveMtextAngleDeg(
  xAxisDirection: { x: number; y: number; z?: number },
  rotationDeg: number,
): number {
  return isCustomMtextXAxisDirection(xAxisDirection)
    ? (Math.atan2(xAxisDirection.y, xAxisDirection.x) * 180) / Math.PI
    : rotationDeg;
}

/**
 * The MTEXT box's LOCAL (unrotated) `{width, height}` — see {@link
 * mtextBounds}'s doc for the width/height priority. Exported separately (not
 * inlined into `mtextBounds`) so `select.ts`'s MText case can build the SAME
 * corner set for its select-geometry points/segments; reverse-deriving a
 * local width/height from the (rotated) AABB `mtextBounds` returns would be
 * WRONG for a non-zero `rotation` (an AABB of a rotated rectangle is larger
 * than the rectangle itself) — both callers must compute width/height from
 * the entity directly.
 *
 * Re-exports `drawing-model`'s `mtextBoxSize` (T1 arm (c) dup:check convergence —
 * `drawing-model`'s own WCS-bounds helper needed the identical width/height priority formula;
 * cad-core already depends on drawing-model, the correct direction, so the formula moved to the
 * one-truth home instead of staying duplicated here).
 *
 * The result is the FULL {@link MtextPaintedExtents} — the two side extents AND the painted
 * y-interval's two ends — not just `{width, height}` plus the sides: {@link anchoredBoxCorners}
 * needs the interval to place the baseline-anchored rectangle the mesh really paints, and
 * narrowing the type here would silently drop it on the way to select geometry (@codex PR #794
 * round 10 P2 finding C's select twin).
 */
export function mtextBoxSize(
  e: MtextEntity,
): { width: number; height: number } & MtextPaintedExtents {
  return mtextBoxSizeShared(e);
}

/**
 * The EFFECTIVE angle (degrees) an MTEXT's box must be rotated by — exported
 * so `select.ts`'s MText case uses the exact same angle `mtextBounds` does
 * (one truth; same reasoning as `mtextBoxSize`'s own export doc above).
 */
export function mtextEffectiveAngleDeg(e: MtextEntity): number {
  return effectiveMtextAngleDeg(e.xAxisDirection, e.rotation ?? 0);
}

/**
 * MTEXT bounds — the rendered box, master representation UNION'd with one box per `kind: "mtext"`
 * annotative context (Codex PR #784 round 8 P2 "Include annotative MTEXT contexts in block bounds").
 *
 * ONE truth — the box is NOT re-derived here: drawing-model's `mtextLocalCorners`
 * (`wcsBoundsLeaf.ts`, the `entityBounds` facet's own MTEXT corner set) applies the identical
 * attachment-grid anchoring, effective-angle (custom group-11 direction precedence over `rotation`),
 * `mtextBoxSize` width/height priority, AND one extra box per annotative context using that context's
 * own insertion / rotation / attachment and per-scale-scaled size — exactly what `mtextFromEntity`
 * renders for the active context (`anno.insertion ?? insertionPoint`, `anno.rotation ?? effective
 * angle`, `nominalHeight × effectiveScale`). A block-child MTEXT whose active context differs from
 * the master then stays inside the INSERT metadata AABB that drives cache-direct culling,
 * fit-to-extents and broad-phase selection — the SAME cross-package reuse `textBounds` established
 * via `textLocalCorners` for the TEXT arm. `mtextBoxSize` / `mtextEffectiveAngleDeg` /
 * `anchoredBoxCorners` remain exported for `select.ts`'s master-only MText case (annotative select
 * geometry is a separate pre-existing gap, same as the TEXT select case).
 */
export function mtextBounds(e: MtextEntity): Aabb | null {
  return boundsFromPoints(mtextLocalCorners(e));
}

/**
 * ATTDEF bounds — mirrors `viewportMembership.ts::textBounds`'s TEXT
 * treatment (insertion + alignment point only, no glyph-box estimate);
 * ATTDEF shares TEXT's structural shape (`insertionPoint`/`alignmentPoint`/
 * `height`/`rotation`) and is not covered at all by `entityModelBounds`
 * (it is a `BlockChildEntity`-only kind — `model.ts`'s `drawingEntitySchema`
 * has no top-level ATTDEF member), which is exactly the drop Codex #467 P2
 * flagged for a block definition containing one.
 */
export function attdefBounds(e: AttdefEntity): Aabb | null {
  return boundsFromPoints(e.alignmentPoint ? [e.insertionPoint, e.alignmentPoint] : [e.insertionPoint]);
}

/**
 * TEXT bounds — the anchor pair (insertion + alignment point, the pair
 * `viewportMembership.ts::textBounds` frames) UNION'd with the rendered
 * glyph box (Codex PR #784 round 3 P1 "TEXT child extents in definition
 * bounds"): a block definition's left-justified label extends `height ×
 * widthFactor × 0.6` per glyph PAST its anchor, so bounding the child by its
 * anchor alone clipped every INSERT whose text runs beyond its line-work —
 * ZOOM E / cull / select saw a box the renderer draws outside of.
 *
 * ONE truth — the box is NOT re-derived here: drawing-model's
 * `textLocalCorners` (`wcsBoundsLeaf.ts`, the `entityBounds` facet's own
 * TEXT corner set) applies the identical justification-aware anchor
 * (`hJustification`/`vJustification`, Aligned/Fit exact-vector width), the
 * render path's `positiveOr` height/widthFactor fallbacks, group-71 mirror +
 * group-51 oblique shear, `rotation`, and one extra box per `kind: "text"`
 * annotative context (the renderer substitutes that context's own
 * insertion/rotation/scaled height per viewport — a document-global bounds
 * must cover every one). The anchor pair is kept in the union so a TEXT
 * still frames at least what it did before (never shrink — the same
 * `anchor ∪ text` shape `childEntityBounds`'s constant-ATTDEF arm uses).
 */
export function textBounds(e: TextEntity): Aabb | null {
  // Without an alignment point the box is anchored AT the insertion point
  // (a corner or edge midpoint of the box), so the corners already frame
  // it; with one, the DXF group-10 insertion point may lie outside the
  // alignment-anchored box and is framed explicitly.
  // The two branches sit on separate lines so the suppression below covers
  // ONLY the equivalent empty-array mutant, not the killable
  // `[insertion, alignment] → []` one (Codex review P3).
  const anchors = e.alignmentPoint
    ? [e.insertionPoint, e.alignmentPoint]
    : // Stryker disable next-line ArrayDeclaration: the only mutant of the empty branch inserts a non-point element, which `boundsFromPoints`' finite-coordinate filter drops — observationally equivalent.
      [];
  return boundsFromPoints([...anchors, ...textLocalCorners(e)]);
}
